import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Snackbar,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ResumeContext from "../OptimiseResume/Resumecontext";
import { appSelector } from "../redux/selector";
import { userSelector } from "../user-redux/selector";
import AdvancedOptimiseTemplateView from "./AdvancedOptimiseTemplateView";
import AdvancedOptimiseAnalysingDialog from "./AdvancedOptimiseAnalysingDialog";
import AdvancedInlineEditor from "./AdvancedInlineEditor";
import useAdvancedOptimise from "./useAdvancedOptimise";
import {
  hydrateAdvancedOptimiseStore,
  clearAdvancedOptimiseStore,
} from "./advancedOptimiseService";
import { mergeOptimizedDataIntoResumeFullDetails } from "./resumeDataUtils";
import { saveAdvancedResume } from "./advancedResumeSave";
import { resumeBuilderSelector } from "../OptimiseResume/ResumeBuilderActions/selector";

const parseResumeIdFromPath = () => {
  const parts = window.location.pathname.split("/");
  const segment = parts[parts.length - 1] || "";
  const match = segment.match(/resume_(\d+)/);
  return match ? match[1] : null;
};

export default function AdvancedOptimiseView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const contextObject = useContext(ResumeContext);
  const contextRef = useRef(contextObject);
  const templateViewRef = useRef(null);
  const { user } = useSelector(userSelector);
  const { selectedCandidate, selectedJob } = useSelector(appSelector);
  const { resumeFullDetails } = useSelector(resumeBuilderSelector);
  const { runAdvancedOptimise, analysing, error, clearError } = useAdvancedOptimise();
  const [hydrated, setHydrated] = useState(false);
  const [missingData, setMissingData] = useState(false);
  const [optimizedData, setOptimizedData] = useState(null);
  const [resumeFullDetailsBase, setResumeFullDetailsBase] = useState(null);
  const [jobTitle, setJobTitle] = useState("");
  const [previewVersion, setPreviewVersion] = useState(0);
  const [activeSection, setActiveSection] = useState("summary");
  const [saving, setSaving] = useState(false);
  const editorPanelRef = useRef(null);

  contextRef.current = contextObject;

  const applyOptimizedData = (nextOptimizedData, baseDetails) => {
    const merged = mergeOptimizedDataIntoResumeFullDetails(baseDetails, nextOptimizedData);
    hydrateAdvancedOptimiseStore(dispatch, contextRef.current, {
      optimizedData: nextOptimizedData,
      resumeFullDetails: merged,
    });
    return merged;
  };

  useEffect(() => {
    const payload = location.state?.advancedOptimisePayload;
    if (!payload?.optimizedData || !payload?.resumeFullDetails) {
      setMissingData(true);
      setHydrated(false);
      return;
    }

    setOptimizedData(payload.optimizedData);
    setResumeFullDetailsBase(payload.resumeFullDetails);
    setJobTitle(location.state?.jobTitle || "");
    applyOptimizedData(payload.optimizedData, payload.resumeFullDetails);
    setPreviewVersion((version) => version + 1);
    setMissingData(false);
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    return () => {
      clearAdvancedOptimiseStore(dispatch, contextRef.current);
    };
  }, [dispatch]);

  const handleSectionSelect = (sectionKey) => {
    setActiveSection(sectionKey);
    editorPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleApplyUpdate = (nextOptimizedData) => {
    setOptimizedData(nextOptimizedData);
    applyOptimizedData(nextOptimizedData, resumeFullDetailsBase);
    setPreviewVersion((version) => version + 1);
  };

  const handleRegenerate = () => {
    const resumeId = parseResumeIdFromPath();
    runAdvancedOptimise({
      resumeId,
      jobDescription: location.state?.jobDescription,
      jobTitle: location.state?.jobTitle,
    });
  };

  const handleSave = async () => {
    if (!optimizedData || saving) return;

    const containerElement = templateViewRef.current?.getContainerElement?.();
    const mergedDetails =
      resumeFullDetails || mergeOptimizedDataIntoResumeFullDetails(resumeFullDetailsBase, optimizedData);

    setSaving(true);
    try {
      await saveAdvancedResume({
        containerElement,
        optimizedData,
        resumeFullDetails: mergedDetails,
        contextObject: contextRef.current,
        user,
        selectedCandidate,
        navigate,
        onExportStart: () => templateViewRef.current?.setExportingPDF?.(true),
        onExportEnd: () => templateViewRef.current?.setExportingPDF?.(false),
        selectedJob: selectedJob
      });
    } catch (err) {
      toast.error(err.message || "Failed to save resume.");
      templateViewRef.current?.setExportingPDF?.(false);
    } finally {
      setSaving(false);
    }
  };

  if (missingData) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No optimised resume data found. Please start from a job details page.
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!hydrated || !optimizedData) {
    return null;
  }

  return (
    <>
      <AdvancedOptimiseAnalysingDialog open={analysing} />

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={clearError} sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>

      <Grid container spacing={0} sx={{ width: "100%", m: 0, p: 0 }}>
        <Grid item xs={12} md={4}>
          <Paper
            ref={editorPanelRef}
            elevation={1}
            sx={{ p: 3, height: "90vh", overflow: "auto", borderRadius: 0 }}
          >
            <AdvancedInlineEditor
              optimizedData={optimizedData}
              jobDescription={location.state?.jobDescription || ""}
              jobTitle={jobTitle}
              selectedSection={activeSection}
              onSectionChange={setActiveSection}
              onApplyUpdate={handleApplyUpdate}
              onBack={() => navigate(-1)}
              onRegenerate={handleRegenerate}
              onSave={handleSave}
              saving={saving}
              regenerating={analysing}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <AdvancedOptimiseTemplateView
            ref={templateViewRef}
            previewVersion={previewVersion}
            activeSection={activeSection}
            onSectionSelect={handleSectionSelect}
            onSave={handleSave}
            saving={saving}
          />
        </Grid>
      </Grid>
    </>
  );
}
